import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiError, STATUS_LABELS, STATUS_STYLES } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STATUS_OPTS = ["pending", "confirmed", "completed", "cancelled"];

export default function Reservations() {
  const qc = useQueryClient();
  const { data: reservations = [], isLoading } = useQuery({ queryKey: ["reservations"], queryFn: () => api.get("/reservations").then((r) => r.data) });
  const [filter, setFilter] = useState("all");

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/reservations/${id}`, { status });
      qc.invalidateQueries({ queryKey: ["reservations"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Durum güncellendi");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = useMemo(() => {
    const list = filter === "all" ? reservations : reservations.filter((r) => r.status === filter);
    return [...list].sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
  }, [reservations, filter]);

  return (
    <div className="space-y-6" data-testid="reservations-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Rezervasyonlar</h1>
          <p className="text-muted-foreground mt-1">Tüm rezervasyonlarınızı yönetin</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 h-11" data-testid="reservation-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="no-reservations">
          Rezervasyon bulunmuyor.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Müşteri</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>Hizmet</TableHead>
                  <TableHead>Personel</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead>Saat</TableHead>
                  <TableHead>Fiyat</TableHead>
                  <TableHead>Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid="reservation-row">
                    <TableCell className="font-medium">{r.customer_name}</TableCell>
                    <TableCell className="text-muted-foreground">{r.customer_phone}</TableCell>
                    <TableCell>{r.service_name}</TableCell>
                    <TableCell className="text-muted-foreground">{r.staff_name || "—"}</TableCell>
                    <TableCell>{r.date}</TableCell>
                    <TableCell>{r.time}</TableCell>
                    <TableCell className="font-semibold">{money(r.service_price)}</TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                        <SelectTrigger className={cn("h-9 w-36 border font-semibold text-xs", STATUS_STYLES[r.status])} data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((r) => (
              <div key={r.id} className="rounded-xl border border-border bg-card p-4" data-testid="reservation-card">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <p className="font-semibold">{r.customer_name}</p>
                    <a href={`tel:${r.customer_phone}`} className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {r.customer_phone}
                    </a>
                  </div>
                  <span className="font-bold">{money(r.service_price)}</span>
                </div>
                <p className="text-sm mt-2">{r.service_name}{r.staff_name ? ` · ${r.staff_name}` : ""} · {r.date} · {r.time}</p>
                <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                  <SelectTrigger className={cn("h-9 mt-3 border font-semibold text-xs", STATUS_STYLES[r.status])} data-testid="status-select-mobile">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
